/**
 * Personal post-its masonry/grid.
 */
import * as React from 'react';
import { PostIt } from '@/types/post-it.types';
import { PostItCard } from '@/components/post-its/PostItCard';
import {
  buildCustomPositions,
  canReorderWithinStatus,
  getActiveColumns,
  getCollapsedVisibleCounts,
  getItemsPerRow,
  getOverdueColumns,
  getPostItHeight,
  partitionPersonalPostIts,
  reorderPostIts,
  sortDonePostIts,
  sortPersonalPostIts,
  sortPostItsByDeadline,
  sortArchivedPostIts,
} from '@/components/post-its/post-it-table-utils';
import type { ElementWidth } from '@/types/interface-elements.types';
import { Button } from '@/components/forms/Button';
import {
  DndContext,
  pointerWithin,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, StickyNote, Trash2 } from 'lucide-react';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { updateInterfaceConfig } from '@/services/preferences.service';
import { LoadingOverlay } from '@/components/ui/loading-overlay';

/** Props for {@link SortablePostIt}. */
interface SortablePostItProps {
  postIt: PostIt;
  postItHeight: number;
  onEdit: (postIt: PostIt) => void;
  onDelete: (id: string) => void;
  onTogglePriority: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onShare: (postIt: PostIt) => void;
  loadingStates?: Record<
    string,
    {
      deleting?: boolean;
      togglingPriority?: boolean;
      togglingStatus?: boolean;
      sharing?: boolean;
    }
  >;
}

/** Draggable personal post-it card with edit, share, and status actions. */
const SortablePostIt: React.FC<SortablePostItProps> = ({
  postIt,
  postItHeight,
  onEdit,
  onDelete,
  onTogglePriority,
  onToggleStatus,
  onShare,
  loadingStates = {},
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: postIt.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  /** Open edit unless this click was a drag. */
  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging) {
      e.stopPropagation();
      onEdit(postIt);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDragging && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onEdit(postIt);
    }
  };

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      className="block w-full p-0 border-0 bg-transparent text-left"
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <PostItCard
        postIt={postIt}
        permission={postIt.userPermission}
        height={postItHeight}
        onDelete={() => onDelete(postIt.id)}
        onTogglePriority={() => onTogglePriority(postIt.id)}
        onToggleStatus={() => onToggleStatus(postIt.id)}
        onShare={() => onShare(postIt)}
        isDragging={isDragging}
        isDeleting={loadingStates[postIt.id]?.deleting}
        isTogglingPriority={loadingStates[postIt.id]?.togglingPriority}
        isTogglingStatus={loadingStates[postIt.id]?.togglingStatus}
        isSharing={loadingStates[postIt.id]?.sharing}
      />
    </button>
  );
};

/** Props for {@link PersonalPostItGrid}. */
interface PersonalPostItGridProps {
  postIts: PostIt[];
  sectionWidth?: ElementWidth;
  editMode?: boolean;
  onReorder?: (postIts: PostIt[]) => void;
  onEdit: (postIt: PostIt) => void;
  onDelete: (id: string, hardDelete?: boolean) => void;
  onTogglePriority: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onShare: (postIt: PostIt) => void;
  onRestore?: (id: string) => void;
  onEmptyTrash?: () => void;
  isLoading?: boolean;
  loadingStates?: Record<
    string,
    {
      deleting?: boolean;
      togglingPriority?: boolean;
      togglingStatus?: boolean;
      sharing?: boolean;
    }
  >;
}

/** Personal post-its masonry/grid. */
export const PersonalPostItGrid: React.FC<PersonalPostItGridProps> = ({
  postIts,
  sectionWidth = 'full',
  editMode: _editMode = false,
  onReorder,
  onEdit,
  onDelete,
  onTogglePriority,
  onToggleStatus,
  onShare,
  onRestore,
  onEmptyTrash,
  isLoading = false,
  loadingStates = {},
}) => {
  const [showCollapsed, setShowCollapsed] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [activePostIt, setActivePostIt] = React.useState<PostIt | null>(null);

  const { getInterfaceConfig, updateInterfaceConfig: updateStore } = usePreferencesStore();
  const postitsConfig = getInterfaceConfig('postits');

  const saveMutation = React.useCallback(
    async (positions: Array<{ id: string; order: number; section: string }>) => {
      updateStore('postits', { positions, sortBy: 'custom' });

      try {
        await updateInterfaceConfig('postits', { positions, sortBy: 'custom' });
      } catch (error) {
        console.error('Failed to save postit positions:', error);
      }
    },
    [updateStore],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  React.useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const itemsPerRow = React.useMemo(() => getItemsPerRow(containerWidth), [containerWidth]);
  const activeColumns = React.useMemo(() => getActiveColumns(itemsPerRow), [itemsPerRow]);
  const overdueColumns = React.useMemo(() => getOverdueColumns(itemsPerRow), [itemsPerRow]);

  const sortedPostIts = React.useMemo(() => {
    const customOrder = (postitsConfig as { positions?: Array<{ id: string; order: number }> })
      ?.positions;
    const sortBy = (postitsConfig as { sortBy?: string } | undefined)?.sortBy;
    return sortPersonalPostIts(postIts, customOrder, sortBy);
  }, [postIts, postitsConfig]);

  const {
    active: rawActive,
    overdue: rawOverdue,
    done,
    archived,
  } = React.useMemo(() => partitionPersonalPostIts(sortedPostIts), [sortedPostIts]);

  const useCustom = (postitsConfig as { sortBy?: string } | undefined)?.sortBy === 'custom';
  const activePostIts = useCustom ? rawActive : sortPostItsByDeadline(rawActive);
  const overduePostIts = useCustom ? rawOverdue : sortPostItsByDeadline(rawOverdue);
  const donePostIts = sortDonePostIts(done);
  const archivedPostIts = sortArchivedPostIts(archived);

  const { maxActiveVisible, maxOverdueVisible } = getCollapsedVisibleCounts(itemsPerRow);
  const visibleActive = activePostIts.slice(0, showCollapsed ? undefined : maxActiveVisible);
  const visibleOverdue = overduePostIts.slice(0, showCollapsed ? undefined : maxOverdueVisible);
  const hasMoreActive = activePostIts.length > maxActiveVisible;
  const hasMoreOverdue = overduePostIts.length > maxOverdueVisible;

  const postItHeight = getPostItHeight(sectionWidth);

  const handleDragStart = (event: DragStartEvent) => {
    const postIt = sortedPostIts.find((p) => p.id === event.active.id);
    setActivePostIt(postIt || null);
  };

  /** Reorder only within the same status column. */
  const handleDragEnd = (event: DragEndEvent) => {
    setActivePostIt(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeItem = sortedPostIts.find((p) => p.id === active.id);
    const overItem = sortedPostIts.find((p) => p.id === over.id);

    if (!canReorderWithinStatus(activeItem, overItem)) {
      if (import.meta.env.DEV) {
        console.warn('Cannot move post-it between different status sections');
      }
      return;
    }

    const newPostIts = reorderPostIts(sortedPostIts, String(active.id), String(over.id));
    void saveMutation(buildCustomPositions(newPostIts));

    if (onReorder) {
      onReorder(newPostIts);
    }
  };

  return (
    <div className="relative">
      <LoadingOverlay isLoading={isLoading} spinnerVariant="default" spinnerSize="lg" />
      <div className="space-y-8 pr-2">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          autoScroll={{ enabled: true, threshold: { x: 0.1, y: 0.15 } }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={containerRef}
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${itemsPerRow}, minmax(240px, 280px))`,
            }}
          >
            <div style={{ gridColumn: `span ${activeColumns}` }} className="space-y-4">
              <h3 className="text-lg font-semibold text-[var(--color-2-nuance-1)]">
                En cours ({activePostIts.length})
              </h3>
              <SortableContext
                items={visibleActive.map((p) => p.id)}
                strategy={rectSortingStrategy}
              >
                {visibleActive.length === 0 ? (
                  <div className="table-empty">
                    <StickyNote className="table-empty-icon" />
                    <p className="table-empty-text">Aucun post-it en cours</p>
                  </div>
                ) : (
                  <div
                    className="grid gap-4"
                    style={{
                      gridTemplateColumns: `repeat(${activeColumns}, minmax(240px, 280px))`,
                    }}
                  >
                    {visibleActive.map((postIt) => (
                      <SortablePostIt
                        key={postIt.id}
                        postIt={postIt}
                        postItHeight={postItHeight}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onTogglePriority={onTogglePriority}
                        onToggleStatus={onToggleStatus}
                        onShare={onShare}
                        loadingStates={loadingStates}
                      />
                    ))}
                  </div>
                )}
              </SortableContext>
            </div>

            <div style={{ gridColumn: `span ${overdueColumns}` }} className="space-y-4">
              <h3 className="text-lg font-semibold text-red-500 dark:text-red-400">
                Dépassés ({overduePostIts.length})
              </h3>
              <SortableContext
                items={visibleOverdue.map((p) => p.id)}
                strategy={rectSortingStrategy}
              >
                {visibleOverdue.length === 0 ? (
                  <div className="table-empty">
                    <StickyNote className="table-empty-icon" />
                    <p className="table-empty-text">Aucun post-it dépassé</p>
                  </div>
                ) : (
                  <div
                    className="grid gap-4"
                    style={{
                      gridTemplateColumns: `repeat(${overdueColumns}, minmax(240px, 280px))`,
                    }}
                  >
                    {visibleOverdue.map((postIt) => (
                      <SortablePostIt
                        key={postIt.id}
                        postIt={postIt}
                        postItHeight={postItHeight}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onTogglePriority={onTogglePriority}
                        onToggleStatus={onToggleStatus}
                        onShare={onShare}
                        loadingStates={loadingStates}
                      />
                    ))}
                  </div>
                )}
              </SortableContext>
            </div>
          </div>

          <DragOverlay dropAnimation={{ duration: 200, easing: 'ease-out' }}>
            {activePostIt && (
              <div className="drag-overlay-base cursor-grabbing">
                <PostItCard
                  postIt={activePostIt}
                  permission={activePostIt.userPermission}
                  height={postItHeight}
                  isDragging
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {(hasMoreActive || hasMoreOverdue) && (
          <div className="flex justify-center">
            <Button
              variant="custom"
              size="sm"
              onClick={() => setShowCollapsed(!showCollapsed)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)] hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_25%,transparent)] text-[var(--color-2-nuance-1)] border border-[var(--color-2-nuance-3)] transition-colors"
            >
              {showCollapsed ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Voir moins
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Voir plus (
                  {(hasMoreActive ? activePostIts.length - maxActiveVisible : 0) +
                    (hasMoreOverdue ? overduePostIts.length - maxOverdueVisible : 0)}{' '}
                  cachés)
                </>
              )}
            </Button>
          </div>
        )}

        {donePostIts.length > 0 && (
          <details className="mt-4">
            <summary
              className="cursor-pointer text-base font-medium text-[var(--color-1-nuance-1)] mb-3 hover:text-[var(--color-2-nuance-1)] transition-colors flex items-center gap-2 px-3 py-3 rounded-lg border"
              style={{
                borderColor: 'color-mix(in srgb, var(--color-1-nuance-2) 40%, transparent)',
              }}
            >
              <ChevronDown className="w-4 h-4 transition-transform [details[open]_&]:rotate-180" />
              <span>Terminés ({donePostIts.length})</span>
            </summary>
            <div
              className="grid gap-4 mt-4"
              style={{
                gridTemplateColumns: `repeat(${itemsPerRow}, minmax(240px, 280px))`,
              }}
            >
              {donePostIts.map((postIt) => (
                <PostItCard
                  key={postIt.id}
                  postIt={postIt}
                  permission={postIt.userPermission}
                  height={postItHeight}
                  onDelete={() => onDelete(postIt.id)}
                  onTogglePriority={() => onTogglePriority(postIt.id)}
                  onToggleStatus={() => onToggleStatus(postIt.id)}
                  onShare={() => onShare(postIt)}
                  onEdit={() => onEdit(postIt)}
                  isDeleting={loadingStates[postIt.id]?.deleting}
                  isTogglingPriority={loadingStates[postIt.id]?.togglingPriority}
                  isTogglingStatus={loadingStates[postIt.id]?.togglingStatus}
                  isSharing={loadingStates[postIt.id]?.sharing}
                />
              ))}
            </div>
          </details>
        )}

        {archivedPostIts.length > 0 && (
          <details className="mt-4">
            <summary
              className="cursor-pointer text-base font-medium text-[var(--color-1-nuance-1)] mb-3 hover:text-[var(--color-2-nuance-1)] transition-colors flex items-center justify-between px-3 py-3 rounded-lg border"
              style={{
                borderColor: 'color-mix(in srgb, var(--color-1-nuance-2) 40%, transparent)',
              }}
            >
              <div className="flex items-center gap-2">
                <ChevronDown className="w-4 h-4 transition-transform [details[open]_&]:rotate-180" />
                <span>Corbeille ({archivedPostIts.length})</span>
              </div>
              {onEmptyTrash && archivedPostIts.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEmptyTrash();
                  }}
                  className="!text-red-600 hover:!text-red-800 hover:!bg-red-100 hover:!border-red-300 !p-1 !h-auto !min-h-0"
                  tooltipText="Vider la corbeille"
                />
              )}
            </summary>
            <div
              className="grid gap-4 mt-4"
              style={{
                gridTemplateColumns: `repeat(${itemsPerRow}, minmax(240px, 280px))`,
              }}
            >
              {archivedPostIts.map((postIt) => (
                <PostItCard
                  key={postIt.id}
                  postIt={postIt}
                  permission={postIt.userPermission}
                  height={postItHeight}
                  isInTrash
                  onDelete={() => onDelete(postIt.id, true)}
                  onRestore={onRestore ? () => onRestore(postIt.id) : undefined}
                  isDeleting={loadingStates[postIt.id]?.deleting}
                  isTogglingStatus={loadingStates[postIt.id]?.togglingStatus}
                />
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};
